import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Plus, MoreVertical, Clock, CheckCircle, Settings, User, Calendar, AlignLeft, Flag, Trash2, Circle, PlayCircle, Atom, Bug, BugOff, ClipboardList, BookUp, Book, Search, X, Archive, Microscope, BrushCleaning } from 'lucide-react';
import { ColumnManagementModal, type KanbanColumn } from './ColumnManagementModal';
import { CardDetailsModal } from './CardDetailsModal';
import { ColumnEditModal } from './ColumnEditModal';
import KanbanStorageAPI from '../../utils/kanbanStorage';
import { smartFormatDateInUserTimezone } from '../../utils/userTimezoneUtils';
import { useKeyboard } from '../../hooks/useKeyboard';
import { useAuth } from '../../contexts/AuthContext';

interface BacklogItem {
  id: string;
  title: string;
  description: string;
  priority: string; // Now dynamic from schema
  type: string; // Now dynamic from schema
  status: string; // Now dynamic based on column IDs
  estimatedEffort: string;
  createdDate: string;
  created?: string; // Alternative field name used by API
  dependencies?: string;
  assignee?: string;
  dueDate?: string;
  isArchived?: boolean;
  archivedDate?: string;
  columnId?: string; // Column assignment field
  // Epic linking fields
  epicId?: string; // ID of the Epic this item is linked to
  linkedItems?: string[]; // For Epic cards: array of linked item IDs
  progress?: number; // For Epic cards: calculated progress percentage
}

const DEFAULT_COLUMNS: KanbanColumn[] = [
  { id: 'backlog', title: 'Backlog', color: 'var(--text-tertiary)', order: 0, isDefault: true },
  { id: 'todo', title: 'To Do', color: 'var(--accent-blue)', order: 1, isDefault: true },
  { id: 'in_progress', title: 'In Progress', color: 'var(--accent-orange)', order: 2, isDefault: true },
  { id: 'done', title: 'Done', color: 'var(--accent-green)', order: 3, isDefault: true }
];

export const KanbanBoard: React.FC = () => {
  const { user } = useAuth();
  const [items, setItems] = useState<BacklogItem[]>([]);
  const [columns, setColumns] = useState<KanbanColumn[]>(DEFAULT_COLUMNS);
  const [loading, setLoading] = useState(true);
  const [showColumnModal, setShowColumnModal] = useState(false);
  const [draggedColumnId, setDraggedColumnId] = useState<string | null>(null);
  const [selectedCard, setSelectedCard] = useState<BacklogItem | null>(null);
  const [showCardModal, setShowCardModal] = useState(false);
  const [selectedColumnForEdit, setSelectedColumnForEdit] = useState<KanbanColumn | null>(null);
  const [showColumnEditModal, setShowColumnEditModal] = useState(false);
  const [isDraggingCard, setIsDraggingCard] = useState(false);
  const [newCardColumnId, setNewCardColumnId] = useState<string | null>(null);
  const [showDatePicker, setShowDatePicker] = useState<string | null>(null); // cardId showing date picker
  const [autoScrollInterval, setAutoScrollInterval] = useState<NodeJS.Timeout | null>(null);
  const [deleteHoverTimeout, setDeleteHoverTimeout] = useState<NodeJS.Timeout | null>(null);
  const [deleteHoverCard, setDeleteHoverCard] = useState<string | null>(null);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState<string | null>(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [cardToDelete, setCardToDelete] = useState<BacklogItem | null>(null);
  const [showArchiveConfirmation, setShowArchiveConfirmation] = useState(false);
  const [cardToArchive, setCardToArchive] = useState<BacklogItem | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [epics, setEpics] = useState<BacklogItem[]>([]);
  const [draggedItem, setDraggedItem] = useState<BacklogItem | null>(null);
  const [dragOverEpic, setDragOverEpic] = useState<string | null>(null);
  
  // Epic linking confirmation modals
  const [showLinkConfirmation, setShowLinkConfirmation] = useState(false);
  const [showUnlinkConfirmation, setShowUnlinkConfirmation] = useState(false);
  const [showLinkWarning, setShowLinkWarning] = useState(false);
  const [pendingLinkAction, setPendingLinkAction] = useState<{item: BacklogItem; epic: BacklogItem} | null>(null);
  const kanbanContainerRef = useRef<HTMLDivElement>(null);
  const kanbanBoardRef = useRef<HTMLDivElement>(null);

  // Keyboard navigation for Epic linking modals
  useKeyboard({
    onEscape: () => {
      if (showLinkConfirmation || showUnlinkConfirmation || showLinkWarning) {
        cancelEpicAction();
      }
    },
    onEnter: () => {
      if (showLinkConfirmation) {
        confirmLinkToEpic();
      } else if (showUnlinkConfirmation) {
        confirmUnlinkFromEpic();
      } else if (showLinkWarning) {
        cancelEpicAction(); // "Got It" button behavior
      }
    },
    enabled: showLinkConfirmation || showUnlinkConfirmation || showLinkWarning,
    enableEnter: true
  });


  // Load backlog items and columns from storage
  useEffect(() => {
    const loadData = async () => {
      // Load both data sources in parallel
      await Promise.all([
        loadKanbanData(),
        loadEpicsData()
      ]);
    };
    loadData();
  }, []);

  useEffect(() => {
    const handleClickOutside = (/* event: MouseEvent */) => {
      if (statusDropdownOpen) {
        setStatusDropdownOpen(null);
      }
    };

    if (statusDropdownOpen) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [statusDropdownOpen]);

  const loadEpicsData = async () => {
    try {
      console.log('🔍 Loading Epic data from API...');
      const response = await fetch('/api/kanban/epics');
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          console.log('✅ Loaded Epic data:', result.data.length, 'epics');
          console.log('📋 Epic titles:', result.data.map((e: any) => ({ id: e.id, title: e.title })));
          setEpics(result.data);
        } else {
          console.error('❌ API returned error:', result);
        }
      } else {
        console.error('❌ API request failed:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('❌ Failed to load Epic data:', error);
    }
  };

  const getEpicTitle = (epicId: string): string => {
    const epic = epics.find(e => e.id === epicId);
    if (epic) {
      // Remove "EPIC:" prefix and trim whitespace
      const title = epic.title.replace(/^EPIC:\s*/i, '').trim();
      console.log('🎯 Found Epic:', epicId, '→', title);
      return title;
    }
    console.log('❌ Epic not found:', epicId, 'in', epics.length, 'epics');
    return epicId; // Fallback to ID if Epic not found
  };

  const getLinkedItemsForEpic = (epicId: string): BacklogItem[] => {
    return items.filter(item => item.epicId === epicId);
  };

  const getEpicStatusIcon = (status: string): string => {
    switch (status) {
      case 'Completed': return '✅';
      case 'In Progress': return '🔄';
      case 'Not Started': return '⭕';
      default: return '❓';
    }
  };

  const linkItemToEpic = async (itemId: string, epicId: string) => {
    try {
      const response = await fetch('/api/kanban/link-epic', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ itemId, epicId })
      });
      
      if (response.ok) {
        console.log('✅ Successfully linked item to Epic');
        // Reload data to show updated relationships
        await Promise.all([loadKanbanData(), loadEpicsData()]);
        return true;
      } else {
        console.error('❌ Failed to link item to Epic');
        return false;
      }
    } catch (error) {
      console.error('❌ Error linking item to Epic:', error);
      return false;
    }
  };

  const canLinkToEpic = (item: BacklogItem, /* epic: BacklogItem */): boolean => {
    // Can't link Epic to itself
    if (item.type === 'Epic') return false;
    // Can only link linkable types (Feature, Bug, Enhancement, Task)
    return ['Feature', 'Bug', 'Enhancement', 'Task'].includes(item.type);
  };

  // Epic linking confirmation handlers
  const handleEpicDrop = (droppedItem: BacklogItem, targetEpic: BacklogItem) => {
    // Scenario 1: Unlinked card dropped on Epic - prompt to link
    if (!droppedItem.epicId) {
      setPendingLinkAction({ item: droppedItem, epic: targetEpic });
      setShowLinkConfirmation(true);
    }
    // Scenario 2: Linked card dropped on the SAME Epic - prompt to unlink
    else if (droppedItem.epicId === targetEpic.id) {
      setPendingLinkAction({ item: droppedItem, epic: targetEpic });
      setShowUnlinkConfirmation(true);
    }
    // Scenario 3: Linked card dropped on a DIFFERENT Epic - warn about existing link
    else {
      setPendingLinkAction({ item: droppedItem, epic: targetEpic });
      setShowLinkWarning(true);
    }
  };

  const confirmLinkToEpic = async () => {
    if (pendingLinkAction) {
      const success = await linkItemToEpic(pendingLinkAction.item.id, pendingLinkAction.epic.id);
      if (success) {
        await loadKanbanData(); // Refresh data
      }
    }
    setShowLinkConfirmation(false);
    setPendingLinkAction(null);
  };

  const confirmUnlinkFromEpic = async () => {
    if (pendingLinkAction) {
      try {
        const response = await fetch('/api/kanban/unlink-epic', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ itemId: pendingLinkAction.item.id })
        });
        
        if (response.ok) {
          await loadKanbanData(); // Refresh data
        }
      } catch (error) {
        console.error('❌ Error unlinking item from Epic:', error);
      }
    }
    setShowUnlinkConfirmation(false);
    setPendingLinkAction(null);
  };

  const cancelEpicAction = () => {
    setShowLinkConfirmation(false);
    setShowUnlinkConfirmation(false);
    setShowLinkWarning(false);
    setPendingLinkAction(null);
  };

  const loadKanbanData = async () => {
    try {
      setLoading(true);
      console.log('Loading Kanban data from filesystem API...');
      
      const response = await fetch('/api/kanban/data');
      if (response.ok) {
        const data = await response.json();
        
        // Extract the actual data structure
        const apiColumns = data.data?.columns || data.columns || [];
        const apiItems = data.data?.items || data.items || [];
        
        // If filesystem data is empty, try migration from localStorage
        if (apiItems.length === 0 && apiColumns.length <= DEFAULT_COLUMNS.length) {
          console.log('🔄 Filesystem data appears empty, attempting migration from localStorage...');
          try {
            const migrated = await KanbanStorageAPI.migrateFromLocalStorage();
            if (migrated) {
              console.log('✅ Migration successful, reloading data...');
              // Reload data after migration
              const newResponse = await fetch('/api/kanban/data');
              if (newResponse.ok) {
                const newData = await newResponse.json();
                setColumns(newData.columns);
                setItems(newData.items);
                console.log('✅ Loaded migrated data:', {
                  columns: newData.columns.length,
                  items: newData.items.length,
                  customColumns: newData.columns.some((col: any) => !col.isDefault)
                });
                return;
              }
            }
          } catch (migrationError) {
            console.warn('Migration failed, using filesystem defaults:', migrationError);
          }
        }
        
        // Transform API column structure to frontend structure
        const transformedColumns = apiColumns.map((col: any) => ({
          id: col.id,
          title: col.title,
          color: col.color,
          order: col.order || col.position || 0, // API uses 'order', fallback to 'position'
          isDefault: col.isDefault || true // Keep original isDefault flag
        }));
        
        setColumns(transformedColumns);
        
        // Transform API items structure to frontend structure
        const transformedItems = Array.isArray(apiItems) ? apiItems : Object.values(apiItems || {});
        setItems(transformedItems);
        console.log('✅ Loaded Kanban data from filesystem:', {
          columns: transformedColumns.length,
          items: transformedItems.length,
          customColumns: transformedColumns.some((col: any) => !col.isDefault)
        });
      } else {
        throw new Error(`API response: ${response.status}`);
      }
    } catch (error) {
      console.error('❌ Failed to load from filesystem API, falling back to localStorage:', error);
      loadFromLocalStorage();
    } finally {
      setLoading(false);
    }
  };

  const loadFromLocalStorage = () => {
    // Fallback to localStorage if API fails
    console.log('📱 Loading from localStorage fallback...');
    const savedColumns = localStorage.getItem('kanban-columns');
    const savedItems = localStorage.getItem('kanban-items');
    
    if (savedColumns) {
      try {
        setColumns(JSON.parse(savedColumns));
      } catch (error) {
        console.error('Failed to parse saved columns:', error);
        setColumns(DEFAULT_COLUMNS);
      }
    } else {
      setColumns(DEFAULT_COLUMNS);
    }
    
    if (savedItems) {
      try {
        setItems(JSON.parse(savedItems));
      } catch (error) {
        console.error('Failed to parse saved items:', error);
      }
    }
  };

  const saveColumns = async (newColumns: KanbanColumn[]) => {
    setColumns(newColumns);
    
    try {
      console.log('💾 Saving columns to filesystem...');
      const response = await fetch('/api/kanban/columns', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ columns: newColumns })
      });
      
      if (response.ok) {
        console.log('✅ Columns saved to filesystem');
      } else {
        throw new Error(`API response: ${response.status}`);
      }
    } catch (error) {
      console.error('❌ Failed to save columns to filesystem, using localStorage fallback:', error);
    }
    
    // Always save to localStorage as backup
    localStorage.setItem('kanban-columns', JSON.stringify(newColumns));
  };

  const saveItems = async (newItems: BacklogItem[]) => {
    setItems(newItems);
    
    try {
      console.log('💾 Saving items to filesystem...');
      const response = await fetch('/api/kanban/items', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: newItems })
      });
      
      if (response.ok) {
        console.log('✅ Items saved to filesystem');
      } else {
        throw new Error(`API response: ${response.status}`);
      }
    } catch (error) {
      console.error('❌ Failed to save items to filesystem, using localStorage fallback:', error);
    }
    
    // Always save to localStorage as backup
    localStorage.setItem('kanban-items', JSON.stringify(newItems));
  };

  const handleDrop = (itemId: string, newColumnId: string) => {
    // Check if the new column ID is valid
    if (!columns.find(col => col.id === newColumnId)) return;
    
    const updatedItems = items.map(item =>
      item.id === itemId
        ? { ...item, columnId: newColumnId }
        : item
    );
    saveItems(updatedItems);
  };

  const handleStatusUpdate = (itemId: string, newStatus: 'Not Started' | 'In Progress' | 'Completed') => {
    const updatedItems = items.map(item =>
      item.id === itemId
        ? { ...item, status: newStatus }
        : item
    );
    saveItems(updatedItems);
  };

  const getStatusIcon = (status: string, itemId: string) => {
    const statusText = status || 'Not Started';
    const isDropdownOpen = statusDropdownOpen === itemId;
    
    const handleStatusClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      setStatusDropdownOpen(isDropdownOpen ? null : itemId);
    };

    const handleStatusSelect = (e: React.MouseEvent, newStatus: 'Not Started' | 'In Progress' | 'Completed') => {
      e.stopPropagation();
      handleStatusUpdate(itemId, newStatus);
      setStatusDropdownOpen(null);
    };

    const getIconElement = () => {
      switch (status) {
        case 'Not Started':
          return <Circle size={20} className="status-icon not-started" />;
        case 'In Progress':
          return <PlayCircle size={20} className="status-icon in-progress" />;
        case 'Completed':
          return <CheckCircle size={20} className="status-icon completed" />;
        default:
          return <Circle size={20} className="status-icon not-started" />;
      }
    };

    return (
      <div className="status-icon-wrapper">
        <div className="status-icon-button" onClick={handleStatusClick} title={statusText}>
          {getIconElement()}
        </div>
        {isDropdownOpen && (
          <div className="status-dropdown">
            <div 
              className="status-option" 
              onClick={(e) => handleStatusSelect(e, 'Not Started')}
            >
              <Circle size={16} className="status-icon not-started" />
              <span>Not Started</span>
            </div>
            <div 
              className="status-option" 
              onClick={(e) => handleStatusSelect(e, 'In Progress')}
            >
              <PlayCircle size={16} className="status-icon in-progress" />
              <span>In Progress</span>
            </div>
            <div 
              className="status-option" 
              onClick={(e) => handleStatusSelect(e, 'Completed')}
            >
              <CheckCircle size={16} className="status-icon completed" />
              <span>Completed</span>
            </div>
          </div>
        )}
      </div>
    );
  };

  const handleDragStart = (e: React.DragEvent, itemId: string) => {
    e.dataTransfer.setData('text/plain', itemId);
    setIsDraggingCard(true);
  };

  const performAutoScroll = useCallback((e: { clientX: number }, isDrag: boolean = false) => {
    if (kanbanContainerRef.current && kanbanBoardRef.current) {
      const container = kanbanContainerRef.current;
      const boardContainer = kanbanBoardRef.current;
      const containerRect = boardContainer.getBoundingClientRect();
      const scrollZone = 150; // pixels from edge to trigger scroll
      const scrollSpeed = isDrag ? 10 : 16;
      const intervalRef = autoScrollInterval;
      const setIntervalRef = setAutoScrollInterval;
      
      // Clear any existing scroll interval
      if (intervalRef) {
        clearInterval(intervalRef);
        setIntervalRef(null);
      }
      
      // Check if mouse is near left or right edge using absolute positioning
      const mouseX = e.clientX;
      const containerLeft = containerRect.left;
      const containerRight = containerRect.right;
      const canScrollLeft = container.scrollLeft > 0;
      const canScrollRight = container.scrollLeft < container.scrollWidth - container.clientWidth;
      
      if ((mouseX - containerLeft) < scrollZone && canScrollLeft) {
        // Scroll left
        const interval = setInterval(() => {
          if (container.scrollLeft <= 0) {
            clearInterval(interval);
            setIntervalRef(null);
          } else {
            container.scrollLeft -= scrollSpeed;
          }
        }, 16);
        setIntervalRef(interval);
      } else if ((containerRight - mouseX) < scrollZone && canScrollRight) {
        // Scroll right
        const interval = setInterval(() => {
          if (container.scrollLeft >= container.scrollWidth - container.clientWidth) {
            clearInterval(interval);
            setIntervalRef(null);
          } else {
            container.scrollLeft += scrollSpeed;
          }
        }, 16);
        setIntervalRef(interval);
      }
    }
  }, [autoScrollInterval, setAutoScrollInterval]);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    
    // Auto-scroll logic for horizontal scrolling during card drag
    if (isDraggingCard) {
      performAutoScroll(e, true);
    }
  };

  const handleDropColumn = (e: React.DragEvent, columnId: string) => {
    e.preventDefault();
    const dataTransfer = e.dataTransfer.getData('text/plain');
    
    // Clear auto-scroll interval
    if (autoScrollInterval) {
      clearInterval(autoScrollInterval);
      setAutoScrollInterval(null);
    }
    setIsDraggingCard(false);
    
    // Check if we're dropping a column (for reordering) or an item
    if (dataTransfer.startsWith('column-')) {
      const draggedColId = dataTransfer.replace('column-', '');
      handleColumnDrop(draggedColId, columnId);
    } else {
      // Regular item drop
      handleDrop(dataTransfer, columnId);
    }
  };

  const handleColumnDragStart = (e: React.DragEvent, columnId: string) => {
    setDraggedColumnId(columnId);
    e.dataTransfer.setData('text/plain', `column-${columnId}`);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleColumnDragEnd = (_e: React.DragEvent) => {
    // Always reset dragged state when drag ends, regardless of whether drop occurred
    setDraggedColumnId(null);
  };

  const handleCardDragEnd = (_e: React.DragEvent) => {
    // Clean up card drag state
    setIsDraggingCard(false);
    if (autoScrollInterval) {
      clearInterval(autoScrollInterval);
      setAutoScrollInterval(null);
    }
  };


  const handleCardClick = (item: BacklogItem) => {
    setSelectedCard(item);
    setShowCardModal(true);
  };

  const handleAddCard = (columnId: string) => {
    setNewCardColumnId(columnId);
    setSelectedCard(null); // Clear selected card for new card creation
    setShowCardModal(true);
  };

  const handleDueDateChange = (cardId: string, newDueDate: string) => {
    const updatedItems = items.map(item =>
      item.id === cardId ? { ...item, dueDate: newDueDate } : item
    );
    saveItems(updatedItems);
    setShowDatePicker(null); // Close date picker
  };

  const handleDeleteHoverStart = (cardId: string) => {
    if (deleteHoverTimeout) {
      clearTimeout(deleteHoverTimeout);
    }
    
    const timeout = setTimeout(() => {
      setDeleteHoverCard(cardId);
    }, 1000); // 1 second hover activation
    
    setDeleteHoverTimeout(timeout);
  };

  const handleDeleteHoverEnd = () => {
    if (deleteHoverTimeout) {
      clearTimeout(deleteHoverTimeout);
      setDeleteHoverTimeout(null);
    }
    setDeleteHoverCard(null);
  };

  const handleDeleteCard = (cardId: string) => {
    const card = items.find(item => item.id === cardId);
    if (card) {
      setCardToDelete(card);
      setShowDeleteConfirmation(true);
      setDeleteHoverCard(null);
    }
  };

  const confirmDeleteCard = async () => {
    if (!cardToDelete) return;
    
    try {
      const updatedItems = items.filter(item => item.id !== cardToDelete.id);
      await saveItems(updatedItems);
      setShowDeleteConfirmation(false);
      setCardToDelete(null);
    } catch (error) {
      console.error('Failed to delete card:', error);
    }
  };

  const cancelDeleteCard = () => {
    setShowDeleteConfirmation(false);
    setCardToDelete(null);
  };

  const handleArchiveCard = (cardId: string) => {
    const card = items.find(item => item.id === cardId);
    if (card) {
      setCardToArchive(card);
      setShowArchiveConfirmation(true);
    }
  };

  const confirmArchiveCard = async () => {
    if (!cardToArchive) return;
    
    try {
      // Archive the card by setting isArchived flag instead of deleting
      const updatedItems = items.map(item => 
        item.id === cardToArchive.id 
          ? { ...item, isArchived: true, archivedDate: new Date().toISOString() }
          : item
      );
      await saveItems(updatedItems);
      setShowArchiveConfirmation(false);
      setCardToArchive(null);
    } catch (error) {
      console.error('Failed to archive card:', error);
    }
  };

  const cancelArchiveCard = () => {
    setShowArchiveConfirmation(false);
    setCardToArchive(null);
  };

  const handleCardSave = (updatedItem: BacklogItem) => {
    if (selectedCard) {
      // Editing existing card
      const updatedItems = items.map(item =>
        item.id === updatedItem.id ? updatedItem : item
      );
      saveItems(updatedItems);
    } else if (newCardColumnId) {
      // Creating new card
      const newCard: BacklogItem = {
        ...updatedItem,
        id: `item_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        columnId: newCardColumnId,
        createdDate: updatedItem.createdDate || new Date().toISOString()
      };
      const updatedItems = [...items, newCard];
      saveItems(updatedItems);
    }
    setShowCardModal(false);
    setSelectedCard(null);
    setNewCardColumnId(null);
  };

  const handleColumnEdit = (column: KanbanColumn) => {
    setSelectedColumnForEdit(column);
    setShowColumnEditModal(true);
  };

  const handleColumnSave = (columnId: string, title: string, color: string) => {
    const updatedColumns = columns.map(col =>
      col.id === columnId ? { ...col, title, color } : col
    );
    setColumns(updatedColumns);
    localStorage.setItem('kanban-columns', JSON.stringify(updatedColumns));
    setShowColumnEditModal(false);
    setSelectedColumnForEdit(null);
  };

  const handleColumnDrop = (draggedColId: string, targetColId: string) => {
    if (draggedColId === targetColId) return;
    
    const draggedColumn = columns.find(col => col.id === draggedColId);
    const targetColumn = columns.find(col => col.id === targetColId);
    
    if (!draggedColumn || !targetColumn) return;
    
    const newColumns = [...columns];
    const draggedIndex = newColumns.findIndex(col => col.id === draggedColId);
    const targetIndex = newColumns.findIndex(col => col.id === targetColId);
    
    // Remove dragged column and insert at target position
    const [removed] = newColumns.splice(draggedIndex, 1);
    newColumns.splice(targetIndex, 0, removed);
    
    // Update order values
    const updatedColumns = newColumns.map((col, index) => ({
      ...col,
      order: index
    }));
    
    setColumns(updatedColumns);
    localStorage.setItem('kanban-columns', JSON.stringify(updatedColumns));
    setDraggedColumnId(null);
  };

  // Filter cards based on search term with advanced syntax support
  const filterCards = (cards: BacklogItem[]) => {
    if (!searchTerm.trim()) return cards;
    
    const searchLower = searchTerm.toLowerCase().trim();
    
    // Check for advanced filter syntax
    const filterPatterns = {
      type: /type:(\S+)/i,
      status: /status:(\S+)/i,
      priority: /priority:(\S+)/i,
      assignee: /assignee:(\S+)/i,
      column: /column:(\S+)/i,
    };
    
    // Extract filters
    const filters: Record<string, string> = {};
    let remainingSearch = searchLower;
    
    Object.entries(filterPatterns).forEach(([key, pattern]) => {
      const match = remainingSearch.match(pattern);
      if (match) {
        filters[key] = match[1].toLowerCase();
        remainingSearch = remainingSearch.replace(pattern, '').trim();
      }
    });
    
    return cards.filter(card => {
      // Apply advanced filters
      if (filters.type && !card.type?.toLowerCase().includes(filters.type)) {
        return false;
      }
      if (filters.status && !card.status?.toLowerCase().includes(filters.status)) {
        return false;
      }
      if (filters.priority && !card.priority?.toLowerCase().includes(filters.priority)) {
        return false;
      }
      if (filters.assignee && !card.assignee?.toLowerCase().includes(filters.assignee)) {
        return false;
      }
      if (filters.column && !card.columnId?.toLowerCase().includes(filters.column)) {
        return false;
      }
      
      // If there's remaining search text after filters, search in all fields
      if (remainingSearch) {
        return (
          card.title?.toLowerCase().includes(remainingSearch) ||
          card.description?.toLowerCase().includes(remainingSearch) ||
          card.assignee?.toLowerCase().includes(remainingSearch) ||
          card.type?.toLowerCase().includes(remainingSearch) ||
          card.priority?.toLowerCase().includes(remainingSearch) ||
          card.id?.toLowerCase().includes(remainingSearch)
        );
      }
      
      // If only filters were specified, return true (passed all filters)
      return true;
    });
  };

  const getTypeIcon = (type: string, status: string) => {
    switch (type) {
      case 'Feature': return <Book size={20} className="type-icon feature" />;
      case 'Bug': 
        return status === 'Completed' 
          ? <BugOff size={20} className="type-icon bug-completed" />
          : <Bug size={20} className="type-icon bug" />;
      case 'Enhancement': return <BookUp size={20} className="type-icon enhancement" />;
      case 'Task': return <ClipboardList size={20} className="type-icon task" />;
      case 'Epic': return <Atom size={20} className="type-icon epic" />;
      case 'Spike': return <Microscope size={20} className="type-icon spike" />;
      case 'Chore': return <BrushCleaning size={20} className="type-icon chore" />;
      default: return <Clock size={20} className="type-icon default" />;
    }
  };

  if (loading) {
    return (
      <div className="kanban-loading">
        <div className="loading-spinner" />
        <p>Loading Kanban board...</p>
      </div>
    );
  }

  return (
    <div className="kanban-container" ref={kanbanBoardRef}>
      <div className="kanban-header">
        <div className="header-content">
          <div>
            <h1>Development Kanban Board</h1>
            <p>Manage development tasks and track progress</p>
          </div>
          <div className="header-controls">
            <div className="search-bar-container">
              <div className="search-input-wrapper">
                <Search size={20} className="search-icon" />
                <input
                  type="text"
                  placeholder="Search or type: status:, priority:..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                  title="Search cards or use filters: type:bug, status:completed, priority:high, assignee:name, column:backlog"
                />
                {searchTerm && (
                  <button
                    className="clear-search-btn"
                    onClick={() => setSearchTerm('')}
                    title="Clear search"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
              {searchTerm && (
                <div className="search-results-count">
                  {filterCards(items).length} of {items.length} cards
                </div>
              )}
            </div>
            <button 
              className="manage-columns-btn"
              onClick={() => setShowColumnModal(true)}
            >
              <Settings size={16} />
              <span>Manage Columns</span>
            </button>
          </div>
        </div>
      </div>

      
      <div 
        className="kanban-board" 
        ref={kanbanContainerRef}
      >
        {[...columns].sort((a, b) => a.order - b.order).map(column => {
          const columnItems = filterCards(items.filter(item => item.columnId === column.id && !item.isArchived));
          if (column.id === 'backlog') {
            console.log('Backlog column items:', columnItems);
          }
          
          return (
            <div
              key={column.id}
              className={`kanban-column ${draggedColumnId === column.id ? 'dragging' : ''}`}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDropColumn(e, column.id)}
            >
              <div 
                className="column-header" 
                style={{ '--column-color': column.color } as React.CSSProperties}
                draggable
                onDragStart={(e) => handleColumnDragStart(e, column.id)}
                onDragEnd={handleColumnDragEnd}
              >
                <div className="column-title">
                  <span style={{ color: column.color }}>{column.title}</span>
                  <span className="item-count">{columnItems.length}</span>
                </div>
                <button 
                  className="column-menu"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleColumnEdit(column);
                  }}
                  title="Edit column"
                >
                  <MoreVertical size={16} />
                </button>
              </div>

              <div className="column-content">
                {columnItems.map(item => (
                  <div
                    key={item.id}
                    className={`clickup-card ${item.type === 'Epic' ? 'epic-card' : ''} ${
                      item.epicId && item.type !== 'Epic' ? 'linked-to-epic' : ''
                    } ${
                      item.type === 'Epic' && dragOverEpic === item.id ? 'epic-drag-over' : ''
                    }`}
                    draggable
                    onDragStart={(e) => {
                      handleDragStart(e, item.id);
                      setDraggedItem(item);
                    }}
                    onDragEnd={(e) => {
                      handleCardDragEnd(e);
                      setDraggedItem(null);
                      setDragOverEpic(null);
                    }}
                    onDragOver={item.type === 'Epic' ? (e) => {
                      if (draggedItem && canLinkToEpic(draggedItem, item)) {
                        e.preventDefault();
                        setDragOverEpic(item.id);
                      }
                    } : undefined}
                    onDragLeave={item.type === 'Epic' ? (_e) => {
                      setDragOverEpic(null);
                    } : undefined}
                    onDrop={item.type === 'Epic' ? (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (draggedItem && canLinkToEpic(draggedItem, item)) {
                        console.log('🔗 Handling Epic drop:', draggedItem.title, 'on Epic', item.title);
                        handleEpicDrop(draggedItem, item);
                      }
                      setDragOverEpic(null);
                    } : undefined}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCardClick(item);
                    }}
                  >
                    <div className="card-content">
                      <div className="card-status-icon">
                        {getStatusIcon(item.status, item.id)}
                      </div>
                      <div className="card-type-icon" title={item.type || 'Unknown'}>
                        {getTypeIcon(item.type, item.status)}
                      </div>
                      <div className="card-title">{item.title}</div>
                      
                      {/* Epic Badge for Feature cards linked to Epic */}
                      {item.epicId && (
                        <div className="epic-badge bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 px-2 py-1 rounded-full text-xs font-medium mb-2 mt-1">
                          🎯 Epic: {getEpicTitle(item.epicId)}
                        </div>
                      )}
                      
                      {/* Epic Progress Display for Epic cards */}
                      {item.type === 'Epic' && item.progress !== undefined && (
                        <div className="epic-progress mb-3 mt-2">
                          <div className="flex justify-between items-center mb-1">
                            <span className="text-xs font-medium text-purple-600 dark:text-purple-400">
                              Progress: {item.progress}%
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                            <div 
                              className="bg-gradient-to-r from-purple-500 to-blue-500 h-1.5 rounded-full transition-all duration-300"
                              style={{ width: `${item.progress}%` }}
                            />
                          </div>
                        </div>
                      )}
                      
                      {/* Linked Items Display for Epic cards */}
                      {item.type === 'Epic' && (
                        <div className="epic-linked-items mt-2 mb-3">
                          {(() => {
                            const linkedItems = getLinkedItemsForEpic(item.id);
                            if (linkedItems.length > 0) {
                              return (
                                <div className="space-y-1">
                                  <div className="text-xs font-semibold text-purple-600 dark:text-purple-400 mb-1">
                                    🔗 Linked Features:
                                  </div>
                                  {linkedItems.map(linkedItem => (
                                    <div key={linkedItem.id} className="flex items-center gap-1 text-xs">
                                      <span>{getEpicStatusIcon(linkedItem.status)}</span>
                                      <span className={`${linkedItem.status === 'Completed' ? 'line-through text-gray-500' : 'text-gray-700 dark:text-gray-300'}`}>
                                        {linkedItem.title.replace(/^FEATURE:\s*/i, '').trim()}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              );
                            }
                            return null;
                          })()}
                        </div>
                      )}
                      
                      <div className="card-middle-row">
                        <button className="card-detail-btn" title={item.description}>
                          <AlignLeft size={14} />
                        </button>
                      </div>
                      
                      <div className="card-actions">
                        <div className="card-actions-left">
                          <button className="card-action-btn" title={item.assignee || "Assign"}>
                            <User size={14} />
                          </button>
                          <div className="date-picker-container">
                            <button 
                              className="card-action-btn" 
                              title={item.dueDate || "Set due date"}
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowDatePicker(showDatePicker === item.id ? null : item.id);
                              }}
                            >
                              <Calendar size={14} />
                            </button>
                            {showDatePicker === item.id && (
                              <div 
                                className="date-picker-popup"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <input
                                  type="date"
                                  value={item.dueDate || ''}
                                  onChange={(e) => handleDueDateChange(item.id, e.target.value)}
                                  onBlur={() => setShowDatePicker(null)}
                                  onClick={(e) => e.stopPropagation()}
                                  autoFocus
                                />
                              </div>
                            )}
                          </div>
                          <div className={`priority-indicator priority-${item.priority?.toLowerCase() || 'medium'}`}>
                            <Flag size={12} />
                            <span>{item.priority || 'Medium'}</span>
                          </div>
                        </div>
                        <div className="card-actions-right">
                          {/* Archive button - only show for completed cards in completed column */}
                          {column.id === 'col_completed' && item.status === 'Completed' && (
                            <button 
                              className="card-archive-btn"
                              title="Archive card (remove from board)"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleArchiveCard(item.id);
                              }}
                            >
                              <Archive size={14} />
                            </button>
                          )}
                          <button 
                            className={`card-delete-btn ${deleteHoverCard === item.id ? 'danger-active' : ''}`}
                            title="Delete card (hover 1s to activate)"
                            onMouseEnter={() => handleDeleteHoverStart(item.id)}
                            onMouseLeave={handleDeleteHoverEnd}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (deleteHoverCard === item.id) {
                                handleDeleteCard(item.id);
                              }
                            }}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                
                {columnItems.length === 0 && (
                  <div className="empty-column">
                    <Clock size={24} style={{ color: 'var(--text-tertiary)' }} />
                    <p>No items</p>
                  </div>
                )}
              </div>

              <button 
                className="add-card-btn"
                onClick={() => handleAddCard(column.id)}
              >
                <Plus size={16} />
                <span>Add item</span>
              </button>
            </div>
          );
        })}
      </div>

      <ColumnManagementModal
        isOpen={showColumnModal}
        onClose={() => setShowColumnModal(false)}
        columns={columns}
        onSave={saveColumns}
      />

      <CardDetailsModal
        isOpen={showCardModal}
        onClose={() => {
          setShowCardModal(false);
          setSelectedCard(null);
          setNewCardColumnId(null);
        }}
        card={selectedCard}
        onSave={handleCardSave}
        columns={columns}
      />

      <ColumnEditModal
        isOpen={showColumnEditModal}
        onClose={() => {
          setShowColumnEditModal(false);
          setSelectedColumnForEdit(null);
        }}
        column={selectedColumnForEdit}
        onSave={handleColumnSave}
      />

      {showDeleteConfirmation && (
        <div className="modal-overlay" onClick={cancelDeleteCard}>
          <div className="modal-content danger-modal" onClick={(e) => e.stopPropagation()}>
            <div className="danger-modal-header">
              <h3>Delete Kanban Card</h3>
              <button className="modal-close" onClick={cancelDeleteCard}>×</button>
            </div>
            
            <div className="danger-modal-body">
              <p><strong>Are you sure you want to delete this card?</strong></p>
              
              {cardToDelete && (
                <div className="card-preview">
                  <div className="preview-title">{cardToDelete.title}</div>
                  {cardToDelete.description && (
                    <div className="preview-description">{cardToDelete.description}</div>
                  )}
                  <div className="preview-meta">
                    <span>Priority: {cardToDelete.priority}</span>
                    {cardToDelete.assignee && <span>Assignee: {cardToDelete.assignee}</span>}
                    {cardToDelete.dueDate && <span>Due: {smartFormatDateInUserTimezone(cardToDelete.dueDate, user)}</span>}
                  </div>
                </div>
              )}
              
              <p className="warning-text">This action cannot be undone.</p>
            </div>
            
            <div className="danger-modal-footer">
              <button className="btn btn-secondary" onClick={cancelDeleteCard}>
                Cancel
              </button>
              <button className="btn btn-danger" onClick={confirmDeleteCard}>
                Delete Card
              </button>
            </div>
          </div>
        </div>
      )}

      {showArchiveConfirmation && (
        <div className="modal-overlay" onClick={cancelArchiveCard}>
          <div className="modal-content archive-modal" onClick={(e) => e.stopPropagation()}>
            <div className="archive-modal-header">
              <h3>Archive Kanban Card</h3>
              <button className="modal-close" onClick={cancelArchiveCard}>×</button>
            </div>
            
            <div className="archive-modal-body">
              <p><strong>Are you sure you want to archive this card?</strong></p>
              
              {cardToArchive && (
                <div className="card-preview">
                  <div className="preview-title">{cardToArchive.title}</div>
                  {cardToArchive.description && (
                    <div className="preview-description">{cardToArchive.description}</div>
                  )}
                  <div className="preview-meta">
                    <span>Priority: {cardToArchive.priority}</span>
                    {cardToArchive.assignee && <span>Assignee: {cardToArchive.assignee}</span>}
                    {cardToArchive.dueDate && <span>Due: {smartFormatDateInUserTimezone(cardToArchive.dueDate, user)}</span>}
                  </div>
                </div>
              )}
              
              <p className="info-text">Archived cards will be removed from the board but kept for analytics. This action can be undone later.</p>
            </div>
            
            <div className="archive-modal-footer">
              <button className="btn btn-secondary" onClick={cancelArchiveCard}>
                Cancel
              </button>
              <button className="btn btn-archive" onClick={confirmArchiveCard}>
                Archive Card
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Epic Link Confirmation Modal */}
      {showLinkConfirmation && pendingLinkAction && (
        <div className="modal-overlay" onClick={cancelEpicAction}>
          <div className="modal-content epic-link-modal" onClick={(e) => e.stopPropagation()}>
            <div className="epic-modal-header">
              <h3>🔗 Link Card to Epic</h3>
              <button className="modal-close" onClick={cancelEpicAction}>×</button>
            </div>
            
            <div className="epic-modal-body">
              <p><strong>Do you want to link this card to the Epic?</strong></p>
              
              <div className="link-preview">
                <div className="card-preview">
                  <div className="preview-label">Card:</div>
                  <div className="preview-title">{pendingLinkAction.item.title}</div>
                  <div className="preview-meta">
                    <span>Type: {pendingLinkAction.item.type}</span>
                    <span>Priority: {pendingLinkAction.item.priority}</span>
                  </div>
                </div>
                
                <div className="link-arrow">→</div>
                
                <div className="epic-preview">
                  <div className="preview-label">Epic:</div>
                  <div className="preview-title epic-title">{pendingLinkAction.epic.title}</div>
                  <div className="preview-meta">
                    <span>Progress: {pendingLinkAction.epic.progress || 0}%</span>
                  </div>
                </div>
              </div>
              
              <p className="info-text">Linking will connect this card to the Epic for progress tracking.</p>
            </div>
            
            <div className="epic-modal-footer">
              <button className="btn btn-secondary" onClick={cancelEpicAction}>
                No, Cancel
              </button>
              <button className="btn btn-epic-link" onClick={confirmLinkToEpic}>
                Yes, Link to Epic
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Epic Unlink Confirmation Modal */}
      {showUnlinkConfirmation && pendingLinkAction && (
        <div className="modal-overlay" onClick={cancelEpicAction}>
          <div className="modal-content epic-unlink-modal" onClick={(e) => e.stopPropagation()}>
            <div className="epic-modal-header">
              <h3>🔓 Unlink Card from Epic</h3>
              <button className="modal-close" onClick={cancelEpicAction}>×</button>
            </div>
            
            <div className="epic-modal-body">
              <p><strong>Do you want to unlink this card from the Epic?</strong></p>
              
              <div className="link-preview">
                <div className="card-preview">
                  <div className="preview-label">Card:</div>
                  <div className="preview-title">{pendingLinkAction.item.title}</div>
                  <div className="preview-meta">
                    <span>Type: {pendingLinkAction.item.type}</span>
                    <span>Currently linked to Epic</span>
                  </div>
                </div>
                
                <div className="unlink-arrow">⚡</div>
                
                <div className="epic-preview">
                  <div className="preview-label">Epic:</div>
                  <div className="preview-title epic-title">{pendingLinkAction.epic.title}</div>
                  <div className="preview-meta">
                    <span>Progress will be recalculated</span>
                  </div>
                </div>
              </div>
              
              <p className="info-text">Unlinking will remove the connection and recalculate Epic progress.</p>
            </div>
            
            <div className="epic-modal-footer">
              <button className="btn btn-secondary" onClick={cancelEpicAction}>
                No, Keep Linked
              </button>
              <button className="btn btn-epic-unlink" onClick={confirmUnlinkFromEpic}>
                Yes, Unlink Card
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Epic Link Warning Modal */}
      {showLinkWarning && pendingLinkAction && (
        <div className="modal-overlay" onClick={cancelEpicAction}>
          <div className="modal-content epic-warning-modal" onClick={(e) => e.stopPropagation()}>
            <div className="epic-modal-header warning-header">
              <h3>⚠️ Card Already Linked to Epic</h3>
              <button className="modal-close" onClick={cancelEpicAction}>×</button>
            </div>
            
            <div className="epic-modal-body">
              <p><strong>This card is already linked to a different Epic!</strong></p>
              
              <div className="warning-preview">
                <div className="card-preview">
                  <div className="preview-label">Card:</div>
                  <div className="preview-title">{pendingLinkAction.item.title}</div>
                  <div className="preview-meta">
                    <span>Type: {pendingLinkAction.item.type}</span>
                    <span className="warning-text">Already linked to another Epic</span>
                  </div>
                </div>
                
                <div className="warning-info">
                  <p>A card can only be linked to one Epic at a time.</p>
                  <p><strong>To link to this Epic:</strong></p>
                  <ol>
                    <li>First unlink the card from its current Epic</li>
                    <li>Then drag it to the new Epic to link</li>
                  </ol>
                </div>
              </div>
            </div>
            
            <div className="epic-modal-footer">
              <button className="btn btn-primary" onClick={cancelEpicAction}>
                Got It
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .kanban-container {
          max-width: 1400px;
          margin: 0 auto;
          padding: 0 20px;
          min-height: 100vh;
          position: relative;
          background: transparent;
        }

        .kanban-header {
          margin-bottom: 32px;
        }

        .header-content {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        
        .header-controls {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .kanban-header h1 {
          font-size: 32px;
          font-weight: 700;
          color: var(--text-primary);
          margin-bottom: 8px;
        }

        .kanban-header p {
          font-size: 16px;
          color: var(--text-secondary);
          margin: 0;
        }

        .manage-columns-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 16px;
          background: var(--bg-primary);
          color: var(--text-primary);
          border: 1px solid var(--border-primary);
          border-radius: var(--radius-medium);
          cursor: pointer;
          font-weight: 500;
          transition: all 0.2s ease;
        }

        .manage-columns-btn:hover {
          background: var(--bg-secondary);
          border-color: var(--border-secondary);
        }

        .search-bar-container {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .search-input-wrapper {
          position: relative;
          width: 350px;
        }

        .search-icon {
          position: absolute;
          left: 16px;
          top: 50%;
          transform: translateY(-50%);
          color: var(--text-tertiary);
          pointer-events: none;
        }

        .search-input {
          width: 100%;
          padding: 11px 40px 11px 40px;
          background: var(--bg-primary);
          border: 1px solid var(--border-primary);
          border-radius: var(--radius-medium);
          font-size: 14px;
          color: var(--text-primary);
          transition: all 0.2s ease;
          height: 40px;
        }

        .search-input:focus {
          outline: none;
          border-color: var(--accent-blue);
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .search-input::placeholder {
          color: var(--text-tertiary);
        }

        .clear-search-btn {
          position: absolute;
          right: 12px;
          top: 50%;
          transform: translateY(-50%);
          padding: 4px;
          background: transparent;
          border: none;
          color: var(--text-tertiary);
          cursor: pointer;
          border-radius: var(--radius-small);
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
        }

        .clear-search-btn:hover {
          background: var(--bg-secondary);
          color: var(--text-primary);
        }

        .search-results-count {
          font-size: 12px;
          color: var(--text-secondary);
          white-space: nowrap;
        }

        .kanban-loading {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          min-height: 400px;
          gap: 16px;
        }

        .loading-spinner {
          width: 32px;
          height: 32px;
          border: 3px solid var(--border-secondary);
          border-top: 3px solid var(--accent-blue);
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        .kanban-board {
          display: flex;
          gap: 24px;
          height: 750px;
          overflow-x: auto;
          padding-bottom: 8px;
        }

        .kanban-column {
          background: var(--bg-primary);
          border-radius: var(--radius-large);
          border: 1px solid var(--border-secondary);
          display: flex;
          flex-direction: column;
          height: 750px;
          min-width: 320px;
          max-width: 320px;
          width: 320px;
          flex-shrink: 0;
          transition: opacity 0.2s ease, transform 0.2s ease;
        }

        .kanban-column.dragging {
          opacity: 0.5;
          transform: rotate(2deg);
        }

        .column-header {
          padding: 16px 20px;
          border-bottom: 1px solid var(--border-secondary);
          display: flex;
          align-items: center;
          justify-content: space-between;
          cursor: grab;
          transition: background 0.2s ease;
          user-select: none;
          border-radius: var(--radius-large) var(--radius-large) 0 0;
          box-shadow: inset 0 3px 0 0 var(--column-color, var(--border-secondary));
        }

        .column-header:hover {
          background: var(--bg-secondary);
        }

        .column-header:active {
          cursor: grabbing;
        }

        .column-title {
          display: flex;
          align-items: center;
          gap: 8px;
          font-weight: 600;
          font-size: 16px;
        }

        .item-count {
          background: var(--bg-secondary);
          color: var(--text-tertiary);
          padding: 2px 8px;
          border-radius: 10px;
          font-size: 12px;
          font-weight: 500;
        }

        .column-menu {
          background: none;
          border: none;
          color: var(--text-tertiary);
          cursor: pointer;
          padding: 4px;
          border-radius: 4px;
          transition: all 0.2s ease;
        }

        .column-menu:hover {
          background: var(--bg-secondary);
          color: var(--text-secondary);
        }

        .column-content {
          flex: 1;
          padding: 16px;
          display: flex;
          flex-direction: column;
          gap: 12px;
          overflow-y: auto;
          max-height: calc(750px - 80px);
        }

        .clickup-card {
          background: var(--bg-primary);
          border: 1px solid var(--border-secondary);
          border-radius: var(--radius-medium);
          cursor: pointer;
          transition: all 0.2s ease;
          padding: 12px;
        }

        .clickup-card:hover {
          box-shadow: var(--shadow-medium);
          transform: translateY(-1px);
        }

        .card-content {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .card-middle-row {
          display: flex;
          align-items: center;
        }

        .card-detail-btn {
          background: none;
          border: none;
          padding: 0;
          color: var(--text-tertiary);
          cursor: help;
          transition: color 0.2s ease;
          display: flex;
          align-items: center;
        }

        .card-detail-btn:hover {
          color: var(--accent-blue);
        }

        .card-content {
          position: relative;
        }

        .card-status-icon {
          position: absolute;
          top: 0;
          right: -4px;
          z-index: 100;
        }

        .card-type-icon {
          position: absolute;
          top: 26px;
          right: -3px;
          z-index: 100;
        }

        .card-title {
          font-size: 13px;
          font-weight: 500;
          color: var(--text-primary);
          line-height: 1.4;
          margin: 0 20px 8px 0;
          word-wrap: break-word;
          word-break: break-word;
          white-space: normal;
          overflow-wrap: break-word;
        }

        .epic-linked-items .text-xs {
          font-size: 13px !important;
          font-weight: 500 !important;
          line-height: 1.4;
        }

        .epic-linked-items .text-xs.font-semibold {
          font-size: 13px !important;
          font-weight: 600 !important;
        }

        .type-icon {
          display: block;
          color: var(--text-secondary);
        }

        .status-icon-wrapper {
          position: relative;
          display: inline-block;
        }

        .status-icon-button {
          cursor: pointer;
          padding: 2px;
          border-radius: 2px;
          transition: background-color 0.2s;
        }

        .status-icon-button:hover {
          background-color: rgba(0, 0, 0, 0.05);
        }

        .status-dropdown {
          position: absolute;
          top: 100%;
          right: 0;
          margin-top: 4px;
          background: var(--bg-primary);
          border: 1px solid var(--border-primary);
          border-radius: 6px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          z-index: 1001;
          min-width: 120px;
        }

        .status-option {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 12px;
          cursor: pointer;
          font-size: 12px;
          color: var(--text-primary);
          transition: background-color 0.2s;
        }

        .status-option:hover {
          background-color: var(--bg-secondary);
        }

        .status-option:first-child {
          border-radius: 6px 6px 0 0;
        }

        .status-option:last-child {
          border-radius: 0 0 6px 6px;
        }

        .status-icon {
          display: block;
        }

        .status-icon.not-started {
          color: var(--text-tertiary);
        }

        .status-icon.in-progress {
          color: var(--accent-blue);
        }

        .status-icon.completed {
          color: var(--accent-green);
        }

        .card-actions {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
        }

        .card-actions-left {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .card-actions-right {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .date-picker-container {
          position: relative;
        }

        .date-picker-popup {
          position: absolute;
          top: 100%;
          left: 0;
          z-index: 1000;
          background: var(--bg-primary);
          border: 1px solid var(--border-primary);
          border-radius: 8px;
          padding: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
          margin-top: 4px;
        }

        .date-picker-popup input[type="date"] {
          border: 1px solid var(--border-secondary);
          border-radius: 4px;
          padding: 6px 8px;
          font-size: 12px;
          color: var(--text-primary);
          background: var(--bg-secondary);
          min-width: 140px;
        }

        .date-picker-popup input[type="date"]:focus {
          outline: none;
          border-color: var(--accent-blue);
          box-shadow: 0 0 0 2px rgba(0, 122, 255, 0.2);
        }

        .date-picker-popup input[type="date"]::-webkit-calendar-picker-indicator {
          cursor: pointer;
        }

        [data-theme="dark"] .date-picker-popup input[type="date"]::-webkit-calendar-picker-indicator {
          filter: invert(1);
        }

        .card-action-btn {
          background: none;
          border: 1px solid var(--border-secondary);
          border-radius: var(--radius-small);
          padding: 4px 8px;
          color: var(--text-tertiary);
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .card-action-btn:hover {
          background: var(--bg-secondary);
          color: var(--text-primary);
          border-color: var(--border-primary);
        }

        .card-archive-btn {
          background: none;
          border: 1px solid var(--border-secondary);
          border-radius: var(--radius-small);
          padding: 4px 8px;
          color: var(--text-tertiary);
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .card-archive-btn:hover {
          background: linear-gradient(135deg, #059669 0%, #047857 100%);
          color: white;
          border-color: #10b981;
          box-shadow: 0 2px 4px rgba(5, 150, 105, 0.2);
        }

        .card-delete-btn {
          background: none;
          border: 1px solid var(--border-secondary);
          border-radius: var(--radius-small);
          padding: 4px 8px;
          color: var(--text-tertiary);
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .card-delete-btn:hover:not(.danger-active) {
          opacity: 1;
          background: linear-gradient(135deg, #111827 0%, #000000 100%);
          border-color: #4b5563;
        }

        .card-delete-btn.danger-active {
          background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
          border-color: #dc2626;
          opacity: 1;
          box-shadow: 0 0 0 2px rgba(220, 38, 38, 0.3);
        }

        .priority-indicator {
          padding: 2px 8px;
          border-radius: var(--radius-small);
          font-size: 11px;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .priority-high {
          background: rgba(255, 59, 48, 0.1);
          color: var(--accent-red);
        }

        .priority-medium {
          background: rgba(255, 149, 0, 0.1);
          color: var(--accent-orange);
        }

        .priority-low {
          background: rgba(0, 122, 255, 0.1);
          color: var(--accent-blue);
        }

        .priority-critical {
          background: rgba(175, 82, 222, 0.1);
          color: var(--accent-purple);
        }

        .empty-column {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 40px 20px;
          color: var(--text-tertiary);
          text-align: center;
        }

        .empty-column p {
          font-size: 14px;
          margin-top: 8px;
        }

        .add-card-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 12px;
          margin: 16px;
          background: var(--bg-secondary);
          border: 1px dashed var(--border-primary);
          border-radius: var(--radius-medium);
          color: var(--text-secondary);
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 14px;
        }

        .add-card-btn:hover {
          background: var(--border-secondary);
          border-style: solid;
        }

        .kanban-board::-webkit-scrollbar {
          height: 8px;
        }

        .kanban-board::-webkit-scrollbar-track {
          background: var(--bg-secondary);
          border-radius: 4px;
        }

        .kanban-board::-webkit-scrollbar-thumb {
          background: var(--border-primary);
          border-radius: 4px;
        }

        .kanban-board::-webkit-scrollbar-thumb:hover {
          background: var(--text-tertiary);
        }

        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .danger-modal {
          background: var(--bg-primary);
          border: 1px solid #dc2626;
          border-radius: var(--radius-medium);
          box-shadow: 0 20px 25px -5px rgba(220, 38, 38, 0.1), 0 10px 10px -5px rgba(220, 38, 38, 0.04);
          max-width: 500px;
          width: 90%;
          max-height: 90vh;
          overflow: hidden;
        }

        .danger-modal-header {
          background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
          color: white;
          padding: 16px 20px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .danger-modal-header h3 {
          margin: 0;
          font-size: 18px;
          font-weight: 600;
        }

        .modal-close {
          background: none;
          border: none;
          color: white;
          font-size: 24px;
          cursor: pointer;
          padding: 0;
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .danger-modal-body {
          padding: 20px;
        }

        .danger-modal-body p {
          margin: 0 0 16px 0;
          color: var(--text-primary);
        }

        .card-preview {
          background: var(--bg-secondary);
          border: 1px solid var(--border-primary);
          border-radius: var(--radius-small);
          padding: 12px;
          margin: 16px 0;
        }

        .preview-title {
          font-weight: 600;
          color: var(--text-primary);
          margin-bottom: 4px;
        }

        .preview-description {
          color: var(--text-secondary);
          font-size: 14px;
          margin-bottom: 8px;
        }

        .preview-meta {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
          font-size: 12px;
          color: var(--text-tertiary);
        }

        .warning-text {
          color: #dc2626;
          font-weight: 500;
          font-size: 14px;
        }

        .danger-modal-footer {
          padding: 16px 20px;
          border-top: 1px solid var(--border-primary);
          display: flex;
          gap: 12px;
          justify-content: flex-end;
        }

        .btn {
          padding: 8px 16px;
          border: none;
          border-radius: var(--radius-small);
          cursor: pointer;
          font-weight: 500;
          transition: all 0.2s ease;
        }

        .btn-secondary {
          background: var(--bg-secondary);
          color: var(--text-primary);
          border: 1px solid var(--border-primary);
        }

        .btn-secondary:hover {
          background: var(--bg-tertiary);
        }

        .btn-danger {
          background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
          color: white;
          border: 1px solid #dc2626;
        }

        .btn-danger:hover {
          background: linear-gradient(135deg, #b91c1c 0%, #991b1b 100%);
          border-color: #b91c1c;
        }

        .archive-modal-header {
          background: linear-gradient(135deg, #059669 0%, #047857 100%);
          color: white;
          padding: 16px 20px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .archive-modal-header h3 {
          margin: 0;
          font-size: 18px;
          font-weight: 600;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .archive-modal-body {
          padding: 20px;
        }

        .info-text {
          color: #059669;
          font-weight: 500;
          font-size: 14px;
        }

        .archive-modal-footer {
          padding: 16px 20px;
          border-top: 1px solid var(--border-primary);
          display: flex;
          gap: 12px;
          justify-content: flex-end;
        }

        .btn-archive {
          background: linear-gradient(135deg, #059669 0%, #047857 100%);
          color: white;
          border: 1px solid #059669;
        }

        .btn-archive:hover {
          background: linear-gradient(135deg, #047857 0%, #065f46 100%);
          border-color: #047857;
        }

        .epic-link-modal,
        .epic-unlink-modal,
        .epic-warning-modal {
          max-width: 600px;
          width: 90vw;
          border-radius: 20px;
          background: var(--bg-primary);
          border: 1px solid var(--border-primary);
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
          backdrop-filter: blur(16px);
          overflow: hidden;
        }

        .epic-modal-header {
          background: linear-gradient(135deg, #AF52DE 0%, #007AFF 100%);
          color: white;
          padding: 20px 24px 16px 24px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          border-radius: 20px 20px 0 0;
        }

        .epic-modal-header.warning-header {
          background: linear-gradient(135deg, #FF9500 0%, #FF6B35 100%);
        }

        .epic-modal-header h3 {
          margin: 0;
          font-size: 16px;
          font-weight: 600;
          display: flex;
          align-items: center;
          justify-content: center;
          letter-spacing: -0.01em;
        }

        .epic-modal-body {
          padding: 24px;
          background: var(--bg-primary);
          color: var(--text-primary);
        }

        .link-preview,
        .warning-preview {
          display: flex;
          align-items: center;
          gap: 20px;
          margin: 20px 0;
          padding: 20px;
          background: var(--bg-secondary);
          border-radius: 16px;
          border: 1px solid var(--border-secondary);
        }

        .card-preview,
        .epic-preview {
          flex: 1;
          padding: 16px;
          background: var(--bg-tertiary);
          border-radius: 12px;
          border: 1px solid var(--border-tertiary);
        }

        .preview-label {
          font-size: 12px;
          font-weight: 700;
          color: var(--text-tertiary);
          text-transform: uppercase;
          margin-bottom: 6px;
          letter-spacing: 0.05em;
        }

        .preview-title {
          font-weight: 600;
          color: var(--text-primary);
          margin-bottom: 8px;
          font-size: 14px;
          line-height: 1.3;
        }

        .epic-title {
          color: #AF52DE;
          font-weight: 700;
        }

        .preview-meta {
          display: flex;
          gap: 12px;
          font-size: 12px;
          color: var(--text-secondary);
          flex-wrap: wrap;
        }

        .preview-meta span {
          padding: 4px 8px;
          background: var(--accent-blue-subtle);
          border-radius: 8px;
          font-weight: 500;
        }

        .warning-text {
          color: #FF9500 !important;
          background: rgba(255, 149, 0, 0.1) !important;
          font-weight: 600;
        }

        .link-arrow,
        .unlink-arrow {
          font-size: 20px;
          font-weight: bold;
          color: #AF52DE;
          display: flex;
          align-items: center;
          justify-content: center;
          min-width: 40px;
        }

        .unlink-arrow {
          color: #FF9500;
        }

        .warning-info {
          flex: 2;
          padding: 20px;
          background: rgba(255, 149, 0, 0.08);
          border-left: 4px solid #FF9500;
          border-radius: 12px;
        }

        .warning-info p {
          margin: 10px 0;
          color: var(--text-primary);
          font-size: 14px;
          line-height: 1.4;
        }

        .warning-info ol {
          margin: 12px 0;
          padding-left: 24px;
          color: var(--text-secondary);
        }

        .warning-info ol li {
          margin: 6px 0;
          line-height: 1.4;
        }
        
        .info-text {
          color: var(--accent-green) !important;
          font-weight: 500;
          font-size: 14px;
          margin-top: 16px;
        }

        .epic-modal-footer {
          padding: 20px 24px;
          border-top: 1px solid var(--border-primary);
          background: var(--bg-primary);
          display: flex;
          gap: 12px;
          justify-content: flex-end;
          border-radius: 0 0 20px 20px;
        }

        .btn-epic-link {
          background: linear-gradient(135deg, #AF52DE 0%, #007AFF 100%);
          color: white;
          border: 1px solid #AF52DE;
          border-radius: 12px;
          padding: 10px 20px;
          font-weight: 600;
          font-size: 14px;
          transition: all 0.2s ease;
        }

        .btn-epic-link:hover {
          background: linear-gradient(135deg, #9D44C2 0%, #0066CC 100%);
          border-color: #9D44C2;
          transform: translateY(-1px);
          box-shadow: 0 8px 16px rgba(175, 82, 222, 0.3);
        }

        .btn-epic-unlink {
          background: linear-gradient(135deg, #FF9500 0%, #FF6B35 100%);
          color: white;
          border: 1px solid #FF9500;
          border-radius: 12px;
          padding: 10px 20px;
          font-weight: 600;
          font-size: 14px;
          transition: all 0.2s ease;
        }

        .btn-epic-unlink:hover {
          background: linear-gradient(135deg, #E68400 0%, #E55A2D 100%);
          border-color: #E68400;
          transform: translateY(-1px);
          box-shadow: 0 8px 16px rgba(255, 149, 0, 0.3);
        }

        .epic-modal-footer .btn-secondary {
          border-radius: 12px;
          padding: 10px 20px;
          font-weight: 600;
          font-size: 14px;
          background: var(--bg-secondary);
          color: var(--text-secondary);
          border: 1px solid var(--border-secondary);
          transition: all 0.2s ease;
        }

        .epic-modal-footer .btn-secondary:hover {
          background: var(--bg-tertiary);
          transform: translateY(-1px);
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
        }

        .epic-modal-footer .btn-primary {
          border-radius: 12px;
          padding: 10px 20px;
          font-weight: 600;
          font-size: 14px;
          background: var(--accent-blue);
          color: white;
          border: 1px solid var(--accent-blue);
          transition: all 0.2s ease;
        }

        .epic-modal-footer .btn-primary:hover {
          background: var(--accent-blue-dark);
          transform: translateY(-1px);
          box-shadow: 0 8px 16px rgba(0, 122, 255, 0.3);
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        @media (max-width: 768px) {
          .kanban-column {
            min-width: 280px;
          }
          
          .header-content {
            flex-direction: column;
            gap: 16px;
            align-items: flex-start;
          }
        }

        /* Epic card styling - subtle background + bright border like linked cards */
        .epic-card {
          background: linear-gradient(135deg, rgba(175, 82, 222, 0.15) 0%, rgba(0, 122, 255, 0.1) 100%);
          border: 2px solid transparent;
          background-clip: padding-box;
          position: relative;
        }

        .epic-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          border-radius: inherit;
          padding: 2px;
          background: linear-gradient(135deg, #AF52DE 0%, #007AFF 100%);
          mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
          mask-composite: xor;
          -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
          -webkit-mask-composite: xor;
          pointer-events: none;
        }

        .epic-card:hover {
          background: linear-gradient(135deg, rgba(175, 82, 222, 0.2) 0%, rgba(0, 122, 255, 0.15) 100%);
          box-shadow: var(--shadow-medium), 0 0 0 4px rgba(175, 82, 222, 0.1);
          transform: translateY(-1px);
        }

        /* Linked card styling - purple gradient border only */
        .clickup-card.linked-to-epic {
          border: 2px solid transparent;
          background: linear-gradient(var(--bg-primary), var(--bg-primary)) padding-box,
                      linear-gradient(135deg, #AF52DE 0%, #007AFF 100%) border-box;
          position: relative;
        }

        .clickup-card.linked-to-epic:hover {
          border: 2px solid transparent;
          background: linear-gradient(var(--bg-primary), var(--bg-primary)) padding-box,
                      linear-gradient(135deg, #AF52DE 0%, #007AFF 100%) border-box;
          box-shadow: var(--shadow-medium), 0 0 0 4px rgba(175, 82, 222, 0.1);
          transform: translateY(-1px);
        }

        .epic-drag-over {
          border: 2px solid transparent;
          background: linear-gradient(var(--bg-secondary), var(--bg-secondary)) padding-box,
                      linear-gradient(135deg, #AF52DE 0%, #007AFF 100%) border-box;
          box-shadow: 0 0 0 4px rgba(175, 82, 222, 0.2);
        }

        /* Bug icon styling - green when completed */
        .type-icon.bug-completed {
          color: #34C759 !important;
        }
      `}</style>
    </div>
  );
};